import Grid from '@mui/material/Grid2';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { IntegrationToolResponse } from 'apis/agency/integrations/types';

type IntegrationPurchaseDrawerProps = {
  integration: any | null;
  tools: IntegrationToolResponse[];
  toolsLoading?: boolean;
  isPaying: boolean;
  onClose: () => void;
  onPay: () => void;
};

const includedBenefits = [
  {
    title: 'Custom Workflows on Request',
    description: "We'll build additional workflows on request, subject to integration's API availability."
  },
  {
    title: 'Default Agent Templates Available',
    description:
      'Each integration includes at least 1 pre-configured agent template, with additional templates available for common use cases.'
  },
  {
    title: 'Custom Agent for Your First Use Case',
    description:
      "We'll craft a bespoke agent tailored to your first business use case, so you're production-ready from day one."
  },
  {
    title: 'First Customer Success Support',
    description: 'Hands-on assistance launching your first customer on this integration.'
  }
];

export default function IntegrationPurchaseDrawer({
  integration,
  tools,
  toolsLoading = false,
  isPaying,
  onClose,
  onPay
}: IntegrationPurchaseDrawerProps) {
  const [confirmPaymentOpen, setConfirmPaymentOpen] = useState(false);
  const oneTimeFee = Number(integration?.one_time_fee || 0);
  const formattedOneTimeFee = `$${oneTimeFee.toLocaleString('en-US')}`;
  const displayedTools = tools || [];
  const canPay = oneTimeFee > 0;

  useEffect(() => {
    if (!integration) setConfirmPaymentOpen(false);
  }, [integration]);

  const handleConfirmPayment = () => {
    setConfirmPaymentOpen(false);
    onPay();
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={Boolean(integration)}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 760 },
            maxWidth: '100%'
          }
        }}
      >
        <Box p={3} height='100%' display='flex' flexDirection='column'>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            pb={2}
            borderBottom='1px solid #E6E6E6'
          >
            <Typography fontSize={20} lineHeight={1.15} fontWeight={500}>
              Get {integration?.name} Voice-Ready
            </Typography>
            <IconButton onClick={onClose}>
              <IoMdClose />
            </IconButton>
          </Box>

          <Box py={3} display='flex' gap={2} alignItems='flex-start'>
            {integration?.avatar_url ? (
              <img
                src={integration?.avatar_url}
                alt={integration?.name}
                style={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 8 }}
              />
            ) : null}
            <Box>
              <Typography fontWeight={500} fontSize={14} lineHeight={1.2}>
                {integration?.name}
              </Typography>
              <Typography color='secondary' fontSize={14} lineHeight={1.45}>
                {integration?.description}
              </Typography>
            </Box>
          </Box>

          <Typography fontWeight={500} fontSize={14} lineHeight={1.2} mb={2}>
            AVAILABLE TOOLS ({displayedTools.length})
          </Typography>
          <Stack direction='row' flexWrap='wrap' gap={1.5} mb={4}>
            {displayedTools.map((tool) => (
              <Chip
                key={tool.name}
                label={tool.name.replace(/_/g, ' ')}
                sx={{ borderRadius: 20, bgcolor: '#F5F5F5', fontSize: 13 }}
              />
            ))}
            {toolsLoading ? <Typography color='secondary'>Loading tools...</Typography> : null}
            {!toolsLoading && !displayedTools.length ? (
              <Typography color='secondary'>No tools available.</Typography>
            ) : null}
          </Stack>

          <Typography fontWeight={500} fontSize={14} lineHeight={1.2} mb={2}>
            WHAT&apos;S INCLUDED IN YOUR {formattedOneTimeFee} ONE-TIME FEE
          </Typography>
          <Grid container spacing={2} mb={4}>
            {includedBenefits.map((benefit) => (
              <Grid key={benefit.title} bgcolor='#F8F9FA' size={{ xs: 12, md: 6 }}>
                <Box border='1px solid #EDEDED' borderRadius={2} p={2.5} height='100%'>
                  <Typography fontWeight={500} fontSize={14} lineHeight={1.2} mb={1}>
                    {benefit.title}
                  </Typography>
                  <Typography color='secondary' fontSize={12} lineHeight={1.5}>
                    {benefit.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography fontWeight={500} fontSize={14} lineHeight={1.2} mb={2}>
            WHAT YOU CAN DO AFTER THIS ONE-TIME PURCHASE
          </Typography>
          <Box border='1px solid #EDEDED' borderRadius={2} p={3} bgcolor='#F8F9FA'>
            <Typography component='div' color='text.primary' fontSize={14} noWrap>
              <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                <li>Sell this integration to unlimited businesses</li>
                <li>
                  Pay wholesale ${integration?.wholesale_price} /connection/location/month and set your own retail price
                  per connection
                </li>
                <li>Pay-as-you-go, no monthly fee when you have no active connections</li>
              </ul>
            </Typography>
          </Box>

          <Box mt='auto' pt={3} display='flex' justifyContent='flex-end' gap={2} pb={4}>
            <Button
              variant='outlined'
              onClick={onClose}
              disabled={isPaying}
              sx={{ borderRadius: 20, textTransform: 'none', minWidth: 120, fontSize: 14 }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              disabled={isPaying || !canPay}
              onClick={() => setConfirmPaymentOpen(true)}
              sx={{ borderRadius: 20, textTransform: 'none', minWidth: 200, fontSize: 14 }}
              startIcon={isPaying ? <CircularProgress size={16} color='inherit' /> : null}
            >
              {isPaying ? 'Processing...' : `Pay ${formattedOneTimeFee} & Activate`}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={confirmPaymentOpen}
        onClose={(_, reason) => {
          if (isPaying) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') setConfirmPaymentOpen(false);
        }}
        maxWidth='xs'
        fullWidth
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(40, 40, 40, 0.72)' } }
        }}
        PaperProps={{
          sx: {
            // borderRadius: 3,
            maxWidth: 420,
            mx: 2
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 3.5, px: 3 }}>
          <Typography fontWeight={700} fontSize={18} color='text.primary' mb={2}>
            Confirm Payment
          </Typography>
          <Typography fontSize={14} fontWeight={400} color='text.primary' lineHeight={1.5} mb={3}>
            Are you sure you want to pay {formattedOneTimeFee} and activate this integration?
          </Typography>
          <Stack direction='row' spacing={2} justifyContent='center' alignItems='center' flexWrap='wrap'>
            <Button
              variant='outlined'
              onClick={() => setConfirmPaymentOpen(false)}
              disabled={isPaying}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                minWidth: 120,
                px: 3,
                fontSize: 14,
                borderColor: '#D9D9D9',
                color: '#757575',
                bgcolor: '#fff',
                '&:hover': { borderColor: '#D9D9D9', bgcolor: '#fafafa' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleConfirmPayment}
              disabled={isPaying}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                minWidth: 120,
                px: 3,
                fontSize: 14
              }}
            >
              Yes Sure
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
